import React, { useState } from 'react';
import { motion, AnimatePresence } from "motion/react";
import "../style/Product.css";
import { productData, inductionProducts } from './data';

const CATEGORIES = [
    { id: 'chimneys', label: 'Chimneys', items: productData, perPage: 6 },
    { id: 'induction', label: 'Electric induction', items: inductionProducts, perPage: 4 },
];

const prefersReducedMotion = () =>
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const Products = () => {
    const [activeId, setActiveId] = useState(CATEGORIES[0].id);
    const [pages, setPages] = useState(
        CATEGORIES.reduce((acc, cat) => ({ ...acc, [cat.id]: 1 }), {})
    );

    const active = CATEGORIES.find((cat) => cat.id === activeId);
    const page = pages[activeId];
    const totalPages = Math.max(1, Math.ceil(active.items.length / active.perPage));
    const start = (page - 1) * active.perPage;
    const currentItems = active.items.slice(start, start + active.perPage);

    const handleTabChange = (id) => {
        if (id === activeId) return;
        setActiveId(id);
    };

    const handlePageChange = (nextPage) => {
        setPages((prev) => ({ ...prev, [activeId]: nextPage }));
    };

    return (
        <section className="ProductBox" id="products">
            <span className="ambientGlow ambientGlow--warm" aria-hidden="true" />
            <span className="ambientGlow ambientGlow--cool" aria-hidden="true" />

            <div className="productsHeader">
                <span className="productsEyebrow">Kitchen hardware</span>
                <h2 className="productsTitle">
                    <span className="titleLight">It's Bronzer's</span>
                    <span className="titleBold">Products</span>
                </h2>
                <p className="productsSubtitle">
                    Extraction and induction systems, engineered for kitchens that don't stop.
                </p>
            </div>

            <div className="tabSwitcher" role="tablist" aria-label="Product categories">
                {CATEGORIES.map((cat) => (
                    <button
                        key={cat.id}
                        role="tab"
                        type="button"
                        aria-selected={activeId === cat.id}
                        className={`tabButton${activeId === cat.id ? ' isActive' : ''}`}
                        onClick={() => handleTabChange(cat.id)}
                    >
                        {activeId === cat.id && (
                            <motion.span
                                layoutId="tabIndicator"
                                className="tabIndicator"
                                transition={{ type: 'spring', stiffness: 420, damping: 34 }}
                            />
                        )}
                        <span className="tabLabel">{cat.label}</span>
                    </button>
                ))}
            </div>

            <AnimatePresence mode="wait">
                <motion.div
                    key={`${activeId}-${page}`}
                    className="productsGrid"
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -18 }}
                    transition={{ duration: 0.35, ease: 'easeOut' }}
                >
                    {currentItems.length > 0 ? (
                        currentItems.map((item, index) => (
                            <ProductCard key={`${item.name}-${index}`} item={item} index={index} />
                        ))
                    ) : (
                        <div className="emptyState">
                            <p>No products in this range yet.</p>
                        </div>
                    )}
                </motion.div>
            </AnimatePresence>

            {totalPages > 1 && (
                <Pagination page={page} totalPages={totalPages} onChange={handlePageChange} />
            )}
        </section>
    );
};

const ProductCard = ({ item, index }) => {
    const handleMove = (e) => {
        if (prefersReducedMotion()) return;
        const card = e.currentTarget;
        const rect = card.getBoundingClientRect();
        const px = (e.clientX - rect.left) / rect.width - 0.5;
        const py = (e.clientY - rect.top) / rect.height - 0.5;
        card.style.setProperty('--rx', `${(py * -7).toFixed(2)}deg`);
        card.style.setProperty('--ry', `${(px * 7).toFixed(2)}deg`);
    };

    const handleLeave = (e) => {
        e.currentTarget.style.setProperty('--rx', `0deg`);
        e.currentTarget.style.setProperty('--ry', `0deg`);
    };

    return (
        <motion.article
            className="productCard"
            onMouseMove={handleMove}
            onMouseLeave={handleLeave}
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.5, delay: index * 0.06, ease: 'easeOut' }}
        >
            {item.tag && <span className="productTag">{item.tag}</span>}

            <div className="productImageWrap">
                <span className="ventMesh" aria-hidden="true" />
                <span className="glowRing" aria-hidden="true" />
                <img
                    className="productImage"
                    src={item.img}
                    alt={item.name}
                    loading={index < 2 ? "eager" : "lazy"}
                    decoding="async"
                />                {item.p && <span className="productPrice">{item.p}</span>}
            </div>

            <div className="productBody">
                <h3 className="productName">{item.name}</h3>

                {item.features && item.features.length > 0 && (
                    <ul className="productFeatures">
                        {item.features.map((feature, i) => (
                            <li key={i}>
                                <span className="featureDot" aria-hidden="true" />
                                {feature}
                            </li>
                        ))}
                    </ul>
                )}

                <a href="#contactUs" className="enquireBtn">
                    <span>Enquire now</span>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path d="M4 12h15M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </a>
            </div>
        </motion.article>
    );
};

const Pagination = ({ page, totalPages, onChange }) => (
    <nav className="pagination" aria-label="Product pages">
        <button
            type="button"
            className="pageArrow"
            onClick={() => onChange(Math.max(1, page - 1))}
            disabled={page === 1}
            aria-label="Previous page"
        >
            &#8249;
        </button>

        <span className="pageTrack">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                <button
                    key={n}
                    type="button"
                    className={`pageDot${n === page ? ' isActive' : ''}`}
                    onClick={() => onChange(n)}
                    aria-current={n === page ? 'page' : undefined}
                >
                    {n}
                </button>
            ))}
        </span>

        <button
            type="button"
            className="pageArrow"
            onClick={() => onChange(Math.min(totalPages, page + 1))}
            disabled={page === totalPages}
            aria-label="Next page"
        >
            &#8250;
        </button>
    </nav>
);

export default Products;